import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { NotificationModel } from '../../mobx/models/notification';
import { NotificationStore } from '../../mobx/stores/notificationStore';
import { updateNotification } from '../../services/inboxServices';
import { captureException } from '../../services/logService';
import { convertToUTCISO, formatToDateString, isEmpty } from '../../helpers/common';
import { rootStore } from '../../mobx/rootStore';
import { UseSnoozeReturn } from '../../interfaces/NotificationInterface';

const { snoozeStore } = rootStore;

export const useSnooze = (
  filteredNotifications: NotificationModel[] | undefined,
  notificationStore: NotificationStore,
  buttonRef: React.MutableRefObject<React.RefObject<HTMLDivElement>[]>,
  mobile: boolean,
  resetSwipe: () => void,
  setIsTaskDetailOpen: (open: boolean) => void
): UseSnoozeReturn => {
  const dropdownRefCalendar = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      snoozeStore.reset();
    };
  }, []);

  // Read observable values as local variables so they are valid React deps
  const isCalendarOpen = snoozeStore.isCalendarOpen;
  const openDropdownId = snoozeStore.openDropdownId;

  const [subDropdownOpen, setSubDropdownOpen] = useState<string>('');

  useEffect(() => {
    // buttonRef is a MutableRefObject with stable identity — intentionally omitted from deps
    if (isCalendarOpen && dropdownRefCalendar?.current && buttonRef.current) {
      if (openDropdownId) {
        const index = filteredNotifications
          ? filteredNotifications.findIndex((item) => item?.id === openDropdownId)
          : -1;

        if (index !== -1 && buttonRef?.current[index] && buttonRef.current[index].current) {
          const buttonRect = buttonRef.current[index].current?.getBoundingClientRect();
          const buttonHeightValue = buttonRef.current[index].current?.offsetHeight;
          const dropdownHeight = dropdownRefCalendar?.current?.offsetHeight || 0;
          snoozeStore.setButtonHeight(buttonHeightValue || 0);

          const spaceBelow = window.innerHeight - (buttonRect?.bottom ?? 0);
          const spaceAbove = buttonRect?.top ?? 0;
          const spaceLeft = buttonRect?.left ?? 0;
          const spaceRight = window.innerWidth - (buttonRect?.right ?? 0);
          const dropdownWidth = dropdownRefCalendar.current?.offsetWidth || 0;

          if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
            snoozeStore.setPosition('top');
            snoozeStore.setDropdownTopOffset((buttonRect?.top ?? 0) - dropdownHeight);
          } else {
            snoozeStore.setPosition('bottom');
            snoozeStore.setDropdownTopOffset(buttonRect?.top ?? 0);
          }

          if (spaceLeft < dropdownWidth && spaceRight < dropdownWidth) {
            snoozeStore.setAlignment('center');
          } else if (spaceRight < dropdownWidth) {
            snoozeStore.setAlignment('right');
          } else {
            snoozeStore.setAlignment('left');
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCalendarOpen, openDropdownId, filteredNotifications]);

  const handleTimeChange = useCallback((value: string) => {
    snoozeStore.setSelectedTime(value);
  }, []);

  const handleClick = useCallback(
    async (key: string | number, notificationId: string) => {
      let payload: { snoozeDate?: string } | undefined;
      switch (key) {
        case 'today':
          payload = { snoozeDate: dayjs().hour(15).minute(0).second(0).toISOString() };
          break;
        case 'tomorrow':
          payload = { snoozeDate: dayjs().add(1, 'day').hour(9).minute(0).second(0).toISOString() };
          break;
        case 'this-Week':
          payload = { snoozeDate: dayjs().endOf('week').hour(9).minute(0).second(0).toISOString() };
          break;
        case 'next-Week':
          payload = {
            snoozeDate: dayjs().day(8).hour(9).minute(0).second(0).toISOString()
          };
          break;
        case 'date-time':
          snoozeStore.setOpenDropdownId(notificationId);
          snoozeStore.setIsCalendarOpen(true);
          break;
        default:
          break;
      }

      if (key !== 'date-time') {
        snoozeStore.setOpenDropdownId(null);
      }

      let result;
      if (payload) {
        const currentTime = new Date().toISOString();

        if (payload?.snoozeDate && payload.snoozeDate < currentTime) {
          rootStore.alertNotificationStore.setErrorMessage('Please choose a date and time in the future.');
          return;
        }
        try {
          result = await updateNotification(notificationId, payload);
        } catch (error) {
          captureException(error);
          rootStore.alertNotificationStore.setErrorMessage('Failed to snooze notification.');
          return;
        }
      }
      if (result) {
        notificationStore.removeNotifications([notificationId]);
      }
    },
    [notificationStore]
  );

  const onClickSnooze = useCallback(async () => {
    if (isEmpty(snoozeStore.selectedDate)) {
      rootStore.alertNotificationStore.setErrorMessage('Please select a date.');
      return;
    }
    if (!snoozeStore.openDropdownId) return;
    const finalCombinedDate = convertToUTCISO(snoozeStore.selectedDate, snoozeStore.selectedTime);

    if (!finalCombinedDate || isNaN(new Date(finalCombinedDate).getTime())) {
      rootStore.alertNotificationStore.setErrorMessage('Invalid final date.');
      return;
    }

    const currentTime = new Date().toISOString();

    if (finalCombinedDate < currentTime) {
      rootStore.alertNotificationStore.setErrorMessage('Please choose a date and time in the future.');
      return;
    }

    const notificationId = snoozeStore.openDropdownId;
    try {
      const result = await updateNotification(notificationId, { snoozeDate: finalCombinedDate });
      if (result) {
        snoozeStore.reset();
        rootStore.alertNotificationStore.setSuccessMessage('Notification snoozed successfully.');
        notificationStore.removeNotifications([notificationId]);
        setIsTaskDetailOpen(false);
      }
    } catch (error) {
      captureException(error);
      rootStore.alertNotificationStore.setErrorMessage('Failed to snooze notification.');
    }
  }, [notificationStore, setIsTaskDetailOpen]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (dropdownRefCalendar.current && !dropdownRefCalendar.current.contains(event.target as Node)) {
      snoozeStore.setIsCalendarOpen(false);
      snoozeStore.setOpenDropdownId(null);
    }
  }, []);

  useEffect(() => {
    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, isCalendarOpen]);

  const onCancelSnooze = useCallback(() => {
    snoozeStore.reset();
  }, []);

  const onClickSnoozeIcon = useCallback(
    (id: string) => {
      if (mobile) resetSwipe();
      snoozeStore.setOpenDropdownId(id);
    },
    [mobile, resetSwipe]
  );

  const onDateChange = useCallback((date: Date) => {
    snoozeStore.setSelectedDate(formatToDateString(date.toISOString()));
  }, []);

  const handleSubDropdownOpen = useCallback(
    (isOpen: boolean, value: string) => {
      if (isOpen) {
        setSubDropdownOpen(value);
      } else {
        setSubDropdownOpen('');
      }
    },
    [setSubDropdownOpen]
  );

  return {
    isCalendarOpen: snoozeStore.isCalendarOpen,
    openDropdownId: snoozeStore.openDropdownId,
    selectedDate: snoozeStore.selectedDate,
    selectedTime: snoozeStore.selectedTime,
    dropdownRefCalendar,
    handleClick,
    handleTimeChange,
    onClickSnooze,
    onCancelSnooze,
    onClickSnoozeIcon,
    onDateChange,
    handleSubDropdownOpen,
    subDropdownOpen,
    setSubDropdownOpen
  };
};

export default useSnooze;
